import { ObservableMap } from '@stencil/store';
import {
    get,
    getEnvironment,
} from '@uipath/portal-shell-util';
import {
    InMemoryWebStorage,
    User,
    UserManager,
    UserManagerSettings,
    WebStorageStateStore,
} from 'oidc-client-ts';

import { isFalselyOrWhiteSpace } from '../store/common';
import { State } from '../store/types/state';
import {
    getEnvConfigField,
    isLocal,
    isMsi,
} from '../utils/EnvironmentUtil';
import { Features } from './featureFlags/featureFlagConstants';
import { getFeatureFlag } from './featureFlags/featureFlags';
import { sanitizeToken } from './helper';
import Logger from './logger/logger';

let userManager: UserManager | null = null;

function getCISUrls(store: ObservableMap<State>, env = getEnvironment(), enableLocal = false) {
    const CISAuthority = getEnvConfigField('CIS_HOST_PREFIX', env, enableLocal) || `https://${window.location.host}/identity${isMsi(store) ? '' : '_'}`;
    const CISHostConnect = `${CISAuthority}/connect`;
    const PortalOrigin = getEnvConfigField('PORTAL_ORIGIN', env, enableLocal) || window.location.origin;
    const CISOpenIdConfiguration = `${CISAuthority}/.well-known/openid-configuration`;
    return {
        CISAuthority,
        CISHostConnect,
        PortalOrigin,
        CISOpenIdConfiguration,
    };
}

function getUserManagerAuthConfiguration(store, env, enableLocal) {
    const {
        CISAuthority, CISHostConnect, PortalOrigin, CISOpenIdConfiguration,
    } = getCISUrls(store, env, enableLocal);

    const clientId = getEnvConfigField('CIS_PKCE_CLIENT_ID', env, enableLocal);

    // Local metadata is separate due to CORs issue accessing openid-configuration endpoint
    const metadata = enableLocal ? {
        metadata: {
            issuer: CISAuthority,
            jwks_uri: `${CISAuthority}/.well-known/openid-configuration/jwks`,
            authorization_endpoint: `${CISHostConnect}/authorize`,
            token_endpoint: `${CISHostConnect}/token`,
            userinfo_endpoint: `${CISHostConnect}/userinfo`,
            end_session_endpoint: `${CISHostConnect}/endsession`,
            check_session_iframe: `${CISHostConnect}/checksession`,
            revocation_endpoint: `${CISHostConnect}/revocation`,
            introspection_endpoint: `${CISHostConnect}/introspect`,
        },
    } : { metadataUrl: CISOpenIdConfiguration };

    const authConfiguration: UserManagerSettings = {
        authority: CISAuthority,
        client_id: clientId,
        post_logout_redirect_uri: `${PortalOrigin}/portal_/sessionexpired`,
        response_type: 'code',
        redirect_uri: `${PortalOrigin}/portal_/api/tokenFactory/callback`,
        silent_redirect_uri: `${PortalOrigin}/portal_/api/tokenFactory/callback`,
        scope: 'openid profile email IdentityServerApi offline_access',
        loadUserInfo: false,
        automaticSilentRenew: true,
        monitorSession: !isLocal(store, env),
        stopCheckSessionOnError: true,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        ...metadata,
    };

    return authConfiguration;
}

function getMsiUserManagerAuthConfiguration(store, env, enableLocal) {
    const {
        CISAuthority, PortalOrigin, CISOpenIdConfiguration,
    } = getCISUrls(store, env, enableLocal);

    const clientId = getEnvConfigField('CIS_PKCE_CLIENT_ID', env, enableLocal);
    const authConfiguration: UserManagerSettings = {
        authority: CISAuthority,
        client_id: clientId,
        post_logout_redirect_uri: `${PortalOrigin}/identity/management/sessionexpired`,
        response_type: 'code',
        redirect_uri: `${PortalOrigin}/identity/management/pkceTokenFactory.html`,
        silent_redirect_uri: `${PortalOrigin}/identity/management/pkceTokenFactory.html`,
        scope: 'openid profile email IdentityServerApi offline_access',
        loadUserInfo: false,
        automaticSilentRenew: true,
        stopCheckSessionOnError: true,
        metadataUrl: CISOpenIdConfiguration,
    };

    return authConfiguration;
}

export function initializeUserManager(store: ObservableMap<State>, enforceSingleSession: boolean, enableLocal: boolean) {
    if (userManager) {
        return userManager;
    }

    try {
        const env = getEnvironment();
        Logger.debug('session', `Environment is ${env}`, { state: store.state });
        const singleSessionEnforcement = isSingleSessionEnforcementEnabled(store, enforceSingleSession);

        const authConfig = isMsi(store)
            ? getMsiUserManagerAuthConfiguration(store, env, enableLocal)
            : getUserManagerAuthConfiguration(store, env, enableLocal);

        userManager = new UserManager(authConfig);
        userManager.events.addUserSignedOut(function() {
            const message = singleSessionEnforcement
                ? `user signed out event, enforcing single session; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`
                : `user signed out event; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`;
            Logger.info('session', message, { state: store.state });
            logout(store);
        });
        userManager.events.addAccessTokenExpired(function() {
            const message = singleSessionEnforcement
                ? `access token expired event, enforcing single session; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`
                : `access token expired event; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`;
            Logger.info('session', message, { state: store.state });
            logout(store);
        });
        userManager.events.addAccessTokenExpiring(function() {
            Logger.info('session', 'access token expiring event', { state: store.state });
        });
        userManager.events.addSilentRenewError(function() {
            Logger.info('session', 'silent renew error event', { state: store.state });
        });
        userManager.events.addUserLoaded(function(user: User) {
            Logger.info('session', 'user loaded event', { state: store.state });
            try {
                setAccessToken(store, user);
            } catch (error) {
                Logger.error('session', `user loaded setAccessToken failed with error: ${error.message}`);
            }
        });
        userManager.events.addUserSessionChanged(function() {
            Logger.info('session', 'user session changed event', { state: store.state });
        });
        userManager.events.addUserSignedIn(function() {
            Logger.info('session', 'user signed in event', { state: store.state });
        });
        userManager.events.addUserUnloaded(function() {
            Logger.info('session', 'user unloaded event', { state: store.state });
        });
        Logger.debug('session', `UserManager initialized successfully: ${!!userManager}`, { state: store.state });
        return userManager;
    } catch (error) {
        Logger.error('session', `failed to initialize UserManager: ${error.message}`);
        return null;
    }
}

export async function logout(store: ObservableMap<State>) {
    try {
        if (!userManager || !store.get('isSignInInitialized')) {
            throw new Error(`UserManager missing: ${!userManager} or is already logged out`);
        }
        Logger.info([ 'session', 'idle timeout' ], 'signing out', { state: store.state });
        store.set('isSignInInitialized', false);
        await userManager.signoutRedirect();
    } catch (error) {
        Logger.error([ 'session', 'idle timeout' ], `sign out error: ${error.message}`);
    }
}

export async function checkSession(token: string, store: ObservableMap<State>) {
    const { CISAuthority } = getCISUrls(store);
    await get(
        `${CISAuthority}/api/checksession`,
        {
            token,
            credentials: 'same-origin',
        },
        (e) => {
            Logger.error('session', `check session error: ${e.message}`);
            throw e;
        },
        () => { /* do nothing on success */ },
    );
}

export function setAccessToken(store: ObservableMap<State>, user: User) {
    if (!user) {
        throw new Error('User not returned after sign in or token renewal');
    }
    if (!user.access_token) {
        throw new Error('User missing access token after sign in or token renewal');
    }

    Logger.debug('session', 'silent login or renewal successful', { state: store.state });
    store.set('selfAuthAccessToken', user.access_token);
}

export function getAccessToken(store: ObservableMap<State>, defaultToken?: string): string {
    Logger.debug('session', `getAccessToken called with sanitized defaultToken: ${sanitizeToken(defaultToken)} and sanitized accessToken: ${sanitizeToken(store.get('accessToken'))}`, { state: store.state });
    const fallbackToken = !isFalselyOrWhiteSpace(defaultToken) ? defaultToken : store.get('accessToken');
    const selfAuthFlag = getFeatureFlag(Features.EnablePortalShellSelfAuth.name);
    if (!selfAuthFlag) {
        Logger.info('session', `getAccessToken returning fallback access token: flag=${selfAuthFlag}, userManager=${!!userManager}, signInInitialized=${store.get('isSignInInitialized')}, defaultToken=${sanitizeToken(defaultToken)}, storeToken=${sanitizeToken(store.get('accessToken'))}, selfAuthToken=${sanitizeToken(store.get('selfAuthAccessToken'))}`, { state: store.state });
        return fallbackToken ?? '';
    }
    const selfAuthAccessToken = store.get('selfAuthAccessToken');
    Logger.debug('session', `returning self auth access token, flag=${selfAuthFlag} selfAuthAccessToken=${sanitizeToken(selfAuthAccessToken)}`, { state: store.state });
    return selfAuthAccessToken;
}

export function isSingleSessionEnforcementEnabled(store: ObservableMap<State>, enforceSingleSessionFlag: boolean) {
    const allowConcurrentSessionsSetting = store.get('account')?.organization?.allowConcurrentSessions;
    return enforceSingleSessionFlag && allowConcurrentSessionsSetting?.toLowerCase() === 'false';
}
